import { useInfiniteQuery, keepPreviousData } from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices";
import { PaginatedResponse } from "@/shared/http";

import { type TriggerEvaluationRecord } from "../../domain/actions";

interface UseTriggerEvaluationsQueryOptions {
  filter: {
    datasetId: string;
  };
  limit?: number;
}

/**
 * Tanstack query key for querying trigger evaluations
 */
export function getQueryKey(options: UseTriggerEvaluationsQueryOptions) {
  const { filter, limit = 50 } = options;
  return ["triggerEvaluations", filter.datasetId, limit];
}

/**
 * Hook to query a dataset's trigger evaluations with pagination support.
 *
 * Uses React Query's infinite query pattern for efficient pagination.
 *
 * @param options - Configuration options
 * @param options.filter.datasetId - Dataset ID to fetch evaluations for
 * @param options.limit - Number of evaluations per page (default: 50)
 *
 * @example
 * ```tsx
 * const evaluationsQuery = useTriggerEvaluationsQuery({
 *   filter: { datasetId: "dataset_123" },
 *   limit: 50,
 * });
 * ```
 */
export function useTriggerEvaluationsQuery(
  options: UseTriggerEvaluationsQueryOptions,
) {
  const { triggerEvaluationService } = useDomainServices();
  const { filter, limit = 50 } = options;
  const { datasetId } = filter;

  return useInfiniteQuery({
    queryKey: getQueryKey(options),
    queryFn: ({ pageParam, signal }) =>
      triggerEvaluationService.getPagedTriggerEvaluationsForDataset(datasetId, {
        abortSignal: signal,
        limit,
        pageToken: pageParam,
      }),
    select: (data) => {
      return data.pages.flatMap((page) => page.items);
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage: PaginatedResponse<TriggerEvaluationRecord>) =>
      lastPage.next_token ?? undefined,
    placeholderData: keepPreviousData,
  });
}
